<template>
  <li v-if="isDivider" class="tree-node-divider"><hr /></li>
  <li
    v-else-if="!node.disabled"
    :id="node.id"
    tabindex="-1"
    role="treeitem"
    aria-selected="false"
    :class="[
      'tree-node',
      {'tree-node-active': (node.id === active?.id || branchActive) && !isIFrame},
    ]"
  >
    <button
      type="button"
      :class="[
        'btn btn-ghost tree-node-label shadow-none',
        {
          active: (node.id === active?.id || branchActive) && !isIFrame,
          'tree-node-excluded': node.meta?.is_excluded,
        },
      ]"
      @click="handleClick"
      @keypress.enter.prevent="handleClick"
    >
      <template v-for="_ in depth" :key="_">
        <span v-show="isLeaf" class="tree-child-node-indent">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="1.5em"
            height="100%"
            viewBox="0 0 24 36"
            preserveAspectRatio="none"
          >
            <rect fill="currentColor" x="11" y="0" width="2" height="36" />
          </svg>
        </span>
      </template>
      <span v-show="$slots.nodeIcon" class="tree-node-icon">
        <slot name="nodeIcon" :node="node" />
      </span>
      <span class="tree-node-label-details"> {{ node.title }}</span>
      <span
        v-if="externalUrl"
        class="tree-parent-node-toggle tree-node-external"
        :title="externalUrl"
      >
        <Icon icon="material-symbols:open-in-new" />
      </span>
      <span v-show="isBranch" :aria-hidden="isBranch === false" class="tree-parent-node-toggle">
        <span
          v-if="node.resolved === false && node.id === active?.id"
          class="spinner-border spinner-border-sm ms-auto align-self-center"
          aria-hidden="true"
        ></span>
        <Icon
          v-else
          icon="material-symbols:arrow-forward-ios"
          :style="{rotate: (expanded ? 90 : 0) + 'deg'}"
        />
      </span>
    </button>
    <TreeView
      v-if="node.children.length > 0 && expanded"
      :tree="node.children"
      role="group"
      :depth="nextDepth"
      class="w-100"
      :expand-all="expandAll"
      :active="active"
      @update:active="updateChildActive"
    >
      <template #nodeIcon="slotProps">
        <slot name="nodeIcon" v-bind="slotProps" />
      </template>
    </TreeView>
    <TeleportTarget v-if="isIFrame" :target-id="`tree-node-iframe-${node.id}`" />
  </li>
</template>
<script setup lang="ts">
/**
 * Recursive tree rendering is costly... obviously.
 * N.B avoid using `v-if`, especially for slotted content. Toggling a v-if block actually tearsdown everything inside.
 * Also avoid passing reactive objects into the tree. Use `Object.freeze()` to improve preformance.
 * Old but relevant @see {@link https://github.com/vuejs/vue/issues/2637}
 */
export type TreeViewNode = {
  id: number;
  title: string;
  children: TreeViewNode[];
  depth?: number;
  expanded: boolean;
  disabled: boolean;
  resolved?: boolean; // Set to true when you've fetched this node's children.
  [key: string]: any; // N.B. For external component use only!
};
const props = defineProps<{
  node: TreeViewNode;
  depth: number;
  expandAll: boolean;
  active?: TreeViewNode;
}>();
const emit = defineEmits<{'update:active': [node: TreeViewNode]}>();
const expanded = ref(props.expandAll || props.node.expanded);
const isBranch = props?.node.children?.length > 0 || props.node.resolved === false;
const isLeaf = props?.depth > 0;
const nextDepth = props.depth + 1;
// Index pages with content have their own page: clicking them navigates *and* toggles the folder.
const hasContent = computed(() => props.node.meta?.has_content === true);
const expand = () => (expanded.value = !expanded.value);
// External links are not pages: open them in a new tab and never become the active node.
const externalUrl = computed(() =>
  props.node.meta?.type === 'cms.ExternalMenuLink' ? props.node.meta.external_url : undefined,
);
const handleClick = () => {
  if (externalUrl.value) {
    window.open(externalUrl.value, '_blank', 'noopener,noreferrer');
    return;
  }
  if (isBranch) {
    expand();
    // Set active when this node fetched it's children. It might want to re-fetch!
    if (props.node.resolved !== undefined || hasContent.value) emit('update:active', props.node);
  } else {
    emit('update:active', props.node);
  }
};
watch(
  [() => props.expandAll, () => props.node.expanded],
  ([newExpandAll, newExpanded], [oldExpandAll, oldExpanded]) => {
    // `expandAll` is always prioritised
    if (newExpandAll !== oldExpandAll && newExpandAll !== expanded.value) expand();
    else if (newExpanded !== oldExpanded) expanded.value = newExpanded;
  },
);
onMounted(() => {
  if (props.node.id === props.active?.id && props.node.children.length === 0)
    emit('update:active', props.node); // If I'm the active node, let my parents know.
});
const activeChild = ref<TreeViewNode>(); // Keep track of my active child.
watchEffect(() => {
  if (props.node.children.length > 0 && props.active?.id !== activeChild.value?.id)
    activeChild.value = undefined; // Reset when active changes.
});
const updateChildActive = (n: TreeViewNode) => {
  activeChild.value = n;
  emit('update:active', n);
};
const branchActive = computed(() => activeChild.value && isBranch && !expanded.value); // I should be active when I have active children.
const isIFrame = computed(
  () => props?.node?.meta?.type === 'cms.IFramePage' && props.node.id === props.active?.id,
);
const isDivider = computed(() => props?.node?.meta?.type === 'cms.MenuDivider');
</script>
<style lang="scss" scoped>
.tree-node-label {
  // The active node is only marked by its title colour, not by a background.
  &.active {
    --bs-btn-active-bg: transparent;
    --bs-btn-active-border-color: transparent;
  }

  display: flex;
  flex: 1;
  align-items: stretch;
  min-block-size: 2em;
  width: 100%;
  padding: 0;
  padding-inline: 0.5rem;
  border: 0;

  &:focus-visible {
    /* N.B When using keyboard: a clicked node keeps focus, which must not look selected */
    outline: 2px solid var(--bs-primary-border-subtle);
    outline-offset: -2px;
  }

  // Page the customer would not normally see (excluded product): a subtle hint only.
  &.tree-node-excluded:not(.active) {
    opacity: 0.6;
  }

  &:disabled {
    pointer-events: none;
    background-color: inherit;

    * {
      color: inherit !important;
      opacity: 0.65; // --bs-btn-disabled-opacity
    }
  }
}

.tree-child-node-indent {
  color: var(--bs-secondary-bg);
  border: 0;
  margin-block-start: 0;
  margin-inline: -0.125rem 0;
}

.tree-node-icon,
.tree-parent-node-toggle {
  color: var(--bs-tertiary-color) !important;
  display: flex;
  align-items: center;
  border: 0;
  block-size: 1.5rem;
  max-inline-size: 1.5rem;
  margin-block-start: 0.375rem;
  margin-inline: -0.25rem 0.25rem;
  padding-inline-start: 0.25rem;
}

.tree-node-icon {
  margin-right: 0.5rem;

  :deep(svg) {
    font-size: 1.5em;
  }
}

.tree-parent-node-toggle {
  margin-left: auto;
}

.tree-node-label-details {
  text-align: left;
  padding: 0.375em 0;
}
</style>
